class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }
}

customElements.define('main-search', MainSearch);

class SakuraFlavorNewestSort {
  constructor() {
    this.abortController = null;
    this.requestKey = '';
    this.observer = new MutationObserver(() => this.refresh());
    this.observer.observe(document.getElementById('ProductGridContainer') || document.body, {
      childList: true,
      subtree: true,
    });
    this.refresh();
  }

  get grid() {
    return document.getElementById('product-grid');
  }

  get isActive() {
    const params = new URLSearchParams(window.location.search);
    return Boolean(this.grid?.dataset.flavorBrand) && params.get('sort_by') === 'created-descending';
  }

  syncSortControls() {
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (select.querySelector('option[value="created-descending"]')) select.value = 'created-descending';
    });
  }

  getPageCount() {
    return Array.from(this.grid.querySelectorAll('.pagination a[href]')).reduce((highest, link) => {
      const page = Number(new URL(link.href, window.location.origin).searchParams.get('page') || 1);
      return Math.max(highest, page);
    }, 1);
  }

  async refresh() {
    if (!this.isActive) {
      this.abortController?.abort();
      this.requestKey = '';
      return;
    }
    this.syncSortControls();
    if (this.grid.dataset.sakuraNewestReady === 'true') return;

    const pageCount = this.getPageCount();
    const params = new URLSearchParams(window.location.search);
    const currentPage = Math.max(1, Number(params.get('page') || 1));
    params.set('section_id', this.grid.dataset.id);
    params.delete('page');
    const requestKey = `${window.location.pathname}?${params.toString()}`;
    if (requestKey === this.requestKey) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.requestKey = requestKey;
    this.grid.setAttribute('aria-busy', 'true');

    try {
      const pages = await Promise.all(
        Array.from({ length: pageCount }, (_, index) => {
          const pageParams = new URLSearchParams(params);
          pageParams.set('page', String(index + 1));
          return fetch(`${window.location.pathname}?${pageParams.toString()}`, {
            signal: this.abortController.signal,
          }).then((response) => {
            if (!response.ok) throw new Error(`Newest sort request failed: ${response.status}`);
            return response.text();
          });
        })
      );

      if (requestKey !== this.requestKey) return;
      const parser = new DOMParser();
      const products = new Map();
      pages.forEach((html) => {
        parser
          .parseFromString(html, 'text/html')
          .querySelectorAll('#product-grid li[data-sakura-product-id][data-sakura-created-at]')
          .forEach((item) => products.set(item.dataset.sakuraProductId, item));
      });

      const ordered = Array.from(products.values()).sort((a, b) =>
        b.dataset.sakuraCreatedAt.localeCompare(a.dataset.sakuraCreatedAt)
      );
      const pageSize = 24;
      const pageItems = ordered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
      const list = this.grid.querySelector('.product-grid');
      if (!list || pageItems.length === 0) throw new Error('Newest sort returned no products');

      list.replaceChildren(...pageItems);
      this.grid.dataset.sakuraNewestReady = 'true';
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('SAKURA newest sort could not be applied.', error);
    } finally {
      if (requestKey === this.requestKey) this.grid?.removeAttribute('aria-busy');
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SakuraFlavorNewestSort(), { once: true });
} else {
  new SakuraFlavorNewestSort();
}
